import { useState, useEffect, useCallback } from "react";

import "../common/Util.css";
import "../common/skeleton-effect.css";
import "./product-list.css";
import { ReactComponent as DownArrowIc } from "../../assets/svg/down_arrow_ics.svg";
import { ReactComponent as RarIc } from "../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../assets/svg/lar_ic.svg";
import { Dropdown, Form } from "react-bootstrap";
import ReactPaginate from "react-paginate";

import { useNavigate } from "react-router-dom";
import DeleteModal from "../common/deleteModel";
import DateRangePicker from "../common/dateRange";
import "react-datepicker/dist/react-datepicker.css";

import { format } from "date-fns";
import { getProductListApi, deleteProductApi } from "./product-api";
import { useGlobal } from "../../GlobalContext";
import editIcon from "../../assets/svg/edit_ic.svg";
import deleteIcon from "../../assets/svg/delete_ic.svg";
import moreIcon from "../../assets/svg/more_ic.svg";
import productIcon from "../../assets/svg/milestone_active.svg";
import { ReactComponent as EmailView } from "../../assets/svg/email_view_ic.svg";
import { ReactComponent as ListView } from "../../assets/svg/list_view_ic.svg";
import { ReactComponent as SelectGrid } from "../../assets/svg/seelct_grid_ic.svg";
import { ReactComponent as SelectList } from "../../assets/svg/select_list_ic.svg";
import { FiPlus, FiSearch } from "react-icons/fi";
import CommonSnackbar from "../common/reusable-commponent";
import { sanitizeInput, sanitizeNumericInput } from "../common/security-utils";

/** Status filter options */
const STATUS_LIST = [
  { value: "", label: "All" },
  { value: true, label: "Active" },
  { value: false, label: "InActive" },
];

/** Sort-by options */
const SORT_BY_OPTIONS = [
  { value: "created asc", label: "Created (Oldest)" },
  { value: "created desc", label: "Created (Newest)" },
  { value: "name asc", label: "Name (A - Z)" },
  { value: "name desc", label: "Name (Z - A)" },
];

/** Default sort option */
const DEFAULT_SORT = { value: "created desc", label: "Created (Newest)" };

export const ProductListing = () => {
  const { rolePermissionList } = useGlobal();
  const navigate = useNavigate();

  // --- Permission helper ---
  const checkPermission = useCallback(
    (permission) =>
      rolePermissionList.some(
        (el) =>
          el["name"] === "Product" &&
          el["menu_permission"] === true &&
          el[permission] === true,
      ),
    [rolePermissionList],
  );

  // --- Filter / sort state ---
  const [search, setSearch] = useState("");
  const [selectedSortby, setSelectedSortBy] = useState(DEFAULT_SORT);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDateSave, setStartDateSave] = useState(null);
  const [endDateSave, setEndDateSave] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // --- Pagination state ---
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // --- Data state ---
  const [productsList, setProductsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // --- UI state ---
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [active, setActive] = useState("list");
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // --- Delete modal state ---
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  // --- Snackbar state ---
  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: null });

  const handleSnackbarClose = useCallback(
    () => setSnackbar((prev) => ({ ...prev, show: false })),
    [],
  );

  const triggerSnackbar = useCallback((message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  }, []);

  // --- Date range handler ---
  const handleDateChange = useCallback(({ start, end }) => {
    setStartDate(start);
    setEndDate(end);
    setStartDateSave(start ? format(start, "yyyy-MM-dd") : null);
    setEndDateSave(end ? format(end, "yyyy-MM-dd") : null);
  }, []);

  // --- Pagination handler ---
  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  // --- Reset filters ---
  const handleResetFilters = useCallback(() => {
    setSelectedStatus("");
    setSearch("");
    setStartDateSave(null);
    setEndDateSave(null);
    setStartDate(null);
    setEndDate(null);
    setSelectedSortBy(DEFAULT_SORT);
  }, []);

  // --- Fetch products ---
  const getProductsList = useCallback(async () => {
    setIsLoading(true);
    const [field, dir] = selectedSortby?.value?.split(" ") || ["created", "desc"];
    const param = {
      limit: itemsPerPage,
      skip: itemOffset * itemsPerPage,
      search: sanitizeInput(search),
      status: selectedStatus ?? "",
      from_date: startDateSave,
      to_date: endDateSave,
      sort_field: field,
      sort_dir: dir,
    };
    try {
      const response = await getProductListApi(param);
      if (response?.data?.success === true) {
        setTotalCount(Number(sanitizeNumericInput(response.data.totalCount ?? 0)));
        setPageCount(Number(sanitizeNumericInput(response.data.totalPages ?? 0)));
        setProductsList(response.data.products ?? []);
      }
    } catch {
      // Error handled by api-service
    } finally {
      setIsLoading(false);
    }
  }, [itemsPerPage, itemOffset, search, selectedSortby, selectedStatus, startDateSave, endDateSave]);

  useEffect(() => {
    getProductsList();
  }, [getProductsList]);

  // --- Delete modal trigger ---
  useEffect(() => {
    if (deleteId !== null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  // --- Delete product ---
  const deleteProduct = useCallback(async () => {
    if (!deleteId) {
      triggerSnackbar("Invalid product ID", false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await deleteProductApi(deleteId);
      if (response?.data?.success === true) {
        getProductsList();
        setDeleteId(null);
        setIsOpenDelete(false);
      }
      if (response?.data) {
        triggerSnackbar(response.data.message || "Operation completed", response.data.success);
      }
    } catch {
      triggerSnackbar("An error occurred during deletion", false);
    } finally {
      setIsLoading(false);
    }
  }, [deleteId, getProductsList, triggerSnackbar]);

  return (
    <>
      <DeleteModal
        title={"product"}
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
        }}
        onDelete={deleteProduct}
      />
      <div>
        <div className="bg-half-white p-2">
          {/* ---------- Toolbar ---------- */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 mt-4">
            <div className="d-flex align-items-center gap-3">
              <h5 className="fs-14 fw-600 text-secondary m-0 product-header-title">
                PRODUCTS : {totalCount}
              </h5>

              <div className="d-flex align-items-center gap-2">
                {/* Status filter */}
                <Dropdown>
                  <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    className="fs-13 fw-500 py-1 px-3 d-flex align-items-center justify-content-between bg-white border rounded product-status-toggle"
                  >
                    Status:
                    {STATUS_LIST.find((s) => s.value === selectedStatus)?.label}
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    {STATUS_LIST.map((item, index) => (
                      <Dropdown.Item
                        key={index}
                        onClick={() => setSelectedStatus(item.value)}
                      >
                        {item.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {/* Date range filter */}
                <div className="d-flex align-items-center px-1 product-date-filter">
                  <span className="fs-12 fw-400 textGrey">Date :</span>
                  <div className="dropdown-text1 ms-2 product-date-picker-offset">
                    <DateRangePicker
                      onChange={handleDateChange}
                      startDate={startDate}
                      endDate={endDate}
                      style={{ border: "none", padding: "0", backgroundColor: "transparent" }}
                    />
                  </div>
                </div>

                {/* Sort-by dropdown */}
                <div onClick={(e) => e.stopPropagation()}>
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="light"
                      id="dropdown-sort"
                      className="d-flex align-items-center justify-content-between product-sort-toggle"
                      title={selectedSortby.label}
                    >
                      <span className="fs-12 fw-400 textGrey">Sort by :</span>
                      <span className="d-flex align-items-center px-1">
                        <span className="dropdown-text">
                          {selectedSortby.label}
                        </span>
                      </span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="product-sort-menu">
                      {SORT_BY_OPTIONS.map((option) => (
                        <Dropdown.Item
                          className="fs-14 fw-500 textcolor"
                          key={option.value}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSortBy(option);
                          }}
                          active={selectedSortby.value === option.value}
                        >
                          {option.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {/* Reset filters */}
                <span
                  className="text-muted text-decoration-underline fs-14 fw-400 product-reset-btn"
                  onClick={handleResetFilters}
                >
                  Reset Filters
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Search input */}
              <div className="position-relative">
                <FiSearch className="position-absolute text-muted product-search-icon" />
                <Form.Control
                  type="text"
                  placeholder=""
                  value={search}
                  maxLength={100}
                  className={`border bg-white product-search-input ${isSearchFocused || search ? "product-search-input--expanded" : "product-search-input--collapsed"}`}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                />
              </div>

              {/* Add Product button */}
              {checkPermission("create_permission") && (
                <div
                  className="fs-13 fw-700 d-flex align-items-center gap-2 rounded product-add-btn"
                  onClick={() => navigate("/product/create")}
                >
                  <FiPlus /> Add Product
                </div>
              )}

              {/* List / Grid toggle */}
              <div className="rounded-3 toggleBg d-flex product-view-toggle">
                <div
                  onClick={() => setActive("list")}
                  className={`d-flex align-items-center justify-content-center rounded-3 product-view-toggle-item ${active === "list" ? "bg-primary1" : ""}`}
                >
                  {active === "list" ? (
                    <ListView className="product-icon-size" />
                  ) : (
                    <SelectList className="product-icon-size" />
                  )}
                </div>

                <div
                  onClick={() => setActive("grid")}
                  className={`d-flex align-items-center justify-content-center rounded-3 product-view-toggle-item ${active === "grid" ? "bg-primary1" : ""}`}
                >
                  {active === "grid" ? (
                    <SelectGrid className="product-icon-size" />
                  ) : (
                    <EmailView className="product-icon-size" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ---------- List View ---------- */}
          {active === "list" && (
            <div className="w-100 trip-table-wrapper bg-white rounded-4">
              {/* Table header */}
              <div className="product-table-header head3 w-100 justify-content-start align-items-center">
                <p className="product-col-1 fs-13 fw-700 textcolor text-start table-header-p">
                  PRODUCT ID
                </p>
                <p className="product-col-2 fs-13 fw-700 textcolor text-start table-header-p">
                  NAME
                </p>
                <p className="product-col-weight fs-13 fw-700 textcolor text-start table-header-p">
                  WEIGHT
                </p>
                <div className="product-dimension-group">
                  <div className="product-dimension-label fs-13 fw-700 textcolor">
                    DIMENSION
                  </div>
                  <div className="product-dimension-sub-row">
                    <div className="product-dimension-sub-cell product-dimension-sub-cell--bordered fs-12 fw-700 textcolor">
                      L
                    </div>
                    <div className="product-dimension-sub-cell product-dimension-sub-cell--bordered fs-12 fw-700 textcolor">
                      B
                    </div>
                    <div className="product-dimension-sub-cell fs-12 fw-700 textcolor">
                      H
                    </div>
                  </div>
                </div>
                <p className="product-col-criteria fs-13 fw-700 textcolor text-start table-header-p">
                  CRITERIA
                </p>
                <p className="product-col-1 fs-13 fw-700 textcolor text-start table-header-p">
                  STATUS
                </p>
              </div>

              {/* Table body */}
              <div>
                {isLoading
                  ? Array.from({ length: itemsPerPage }).map((_, index) => (
                    <div
                      key={index}
                      className="product-table-row head3 w-100 justify-content-center align-items-center"
                    >
                      <div className="product-col-1 d-flex align-items-center">
                        <div className="skeleton skeleton-text" style={{ width: "80px", height: "14px" }} />
                      </div>
                      <div className="product-col-2 d-flex align-items-center">
                        <div className="skeleton skeleton-text" style={{ width: "140px", height: "14px" }} />
                      </div>
                      <div className="product-col-1 d-flex align-items-center">
                        <div className="skeleton skeleton-text" style={{ width: "60px", height: "14px" }} />
                      </div>
                      <div className="product-dimension-data">
                        <div className="product-dimension-data-cell product-dimension-data-cell--bordered">
                          <div className="skeleton skeleton-text" style={{ width: "40px", height: "14px" }} />
                        </div>
                        <div className="product-dimension-data-cell product-dimension-data-cell--bordered">
                          <div className="skeleton skeleton-text" style={{ width: "40px", height: "14px" }} />
                        </div>
                        <div className="product-dimension-data-cell">
                          <div className="skeleton skeleton-text" style={{ width: "40px", height: "14px" }} />
                        </div>
                      </div>
                      <div className="product-col-criteria d-flex align-items-center">
                        <div className="skeleton skeleton-text" style={{ width: "80px", height: "14px" }} />
                      </div>
                      <div className="product-col-1 d-flex align-items-center">
                        <div className="skeleton product-skeleton-status" />
                      </div>
                    </div>
                  ))
                  : (productsList ?? []).map((item, index) => (
                    <div
                      key={item.id ?? index}
                      className={`product-table-row product-table-row--data head3 w-100 justify-content-center align-items-center ${hoveredIndex === index ? "product-table-row--hovered" : ""}`}
                      onClick={() => {
                        if (checkPermission("view_permission") && item.id) {
                          navigate("/product/view", {
                            state: { id: item.id },
                          });
                        }
                      }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <p
                        className="product-col-1 product-table-cell fs-13 fw-500 textcolor text-start table-row-p"
                        title={sanitizeInput(item.item_code ?? "-")}
                      >
                        {sanitizeInput(item.item_code ?? "---")}
                      </p>

                      <p
                        className="product-col-2 product-table-cell fs-13 fw-500 textcolor text-start table-row-p"
                        title={sanitizeInput(item.item_name ?? "")}
                      >
                        {sanitizeInput(item.item_name ?? "---")}
                      </p>

                      <p
                        className="product-col-1 product-table-cell fs-13 fw-500 textcolor text-start table-row-p"
                        title={sanitizeNumericInput(item.weight) || "-"}
                      >
                        {sanitizeNumericInput(item.weight) || "-"}
                      </p>

                      <div
                        className="product-dimension-data"
                        title={`${sanitizeNumericInput(item.length) || "-"} x ${sanitizeNumericInput(item.width) || "-"} x ${sanitizeNumericInput(item.height) || "-"}`}
                      >
                        <div className="product-dimension-data-cell product-dimension-data-cell--bordered fs-13 fw-500 textcolor">
                          {sanitizeNumericInput(item.length) || "-"}{" "}
                          {item.length > 0 && item.measurement_type
                            ? `(${sanitizeInput(item.measurement_type)})`
                            : ""}
                        </div>
                        <div className="product-dimension-data-cell product-dimension-data-cell--bordered fs-13 fw-500 textcolor">
                          {sanitizeNumericInput(item.width) || "-"}
                          {item.width > 0 && item.measurement_type
                            ? `(${sanitizeInput(item.measurement_type)})`
                            : ""}
                        </div>
                        <div className="product-dimension-data-cell fs-13 fw-500 textcolor">
                          {sanitizeNumericInput(item.height) || "-"}
                          {item.height > 0 && item.measurement_type
                            ? `(${sanitizeInput(item.measurement_type)})`
                            : ""}
                        </div>
                      </div>

                      <p
                        className="product-col-criteria product-table-cell fs-13 fw-500 textcolor text-start table-row-p"
                        title={sanitizeInput(item.criteria ?? "")}
                      >
                        {sanitizeInput(item.criteria ?? "--")}
                      </p>

                      <p className="product-col-1 product-table-cell fs-13 fw-500 textcolor text-start table-row-p">
                        <span
                          className={`product-status-badge ${item.status ? "product-status-badge--active" : "product-status-badge--inactive"}`}
                        >
                          {item.status ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </p>

                      {hoveredIndex === index && (
                        <div className="product-action-overlay">
                          <div className="product-action-row">
                            {checkPermission("edit_permission") && (
                              <div
                                className="product-action-icon-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/product/create", {
                                    state: { id: item.id },
                                  });
                                }}
                              >
                                <img src={editIcon} alt="edit" />
                              </div>
                            )}
                            <span className="product-action-spacer" />
                            {checkPermission("delete_permission") && (
                              <div
                                className="product-action-icon-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteId(item.id);
                                }}
                              >
                                <img src={deleteIcon} alt="delete" />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* ---------- Grid View ---------- */}
          {active === "grid" &&
            (isLoading
              ? Array.from({ length: itemsPerPage }).map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-4 d-flex flex-wrap align-items-center justify-content-between w-100 p-3 mb-3"
                >
                  <div className="col-12 col-md-6 col-lg-3 d-flex flex-grow-1 me-2 product-grid-divider">
                    <div className="skeleton product-skeleton-img" />
                    <div className="d-flex flex-column justify-content-center gap-2 ps-3">
                      <div className="skeleton skeleton-text product-skeleton-title" />
                      <div className="skeleton skeleton-text-sm product-skeleton-subtitle" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 col-lg-3 ms-3 d-flex flex-column flex-grow-1 align-items-start me-1 product-grid-divider">
                    <div className="skeleton skeleton-text-sm mb-2 product-skeleton-detail-sm" />
                    <div className="skeleton skeleton-text-sm product-skeleton-detail-lg" />
                  </div>

                  <div className="d-flex flex-row flex-grow-1 align-items-end justify-content-end gap-2 ps-3">
                    <div className="skeleton product-skeleton-circle" />
                  </div>
                </div>
              ))
              : productsList.map((item, index) => (
                <div
                  key={item.id ?? index}
                  className="bg-white rounded-4 d-flex flex-wrap align-items-center justify-content-between business-card w-100 p-3 mb-3 product-grid-card"
                  onClick={() => {
                    if (checkPermission("view_permission") && item.id) {
                      navigate("/product/view", {
                        state: { id: item.id },
                      });
                    }
                  }}
                >
                  <div className="col-12 col-md-6 col-lg-3 d-flex flex-grow-1 me-2 product-grid-divider">
                    <div className="profile-wrapper product-grid-profile-bg">
                      <img
                        key={index}
                        src={productIcon}
                        alt="product"
                        className="profile-img"
                      />
                    </div>
                    <div className="d-flex flex-column justify-content-center align-items-start gap-1 ps-3">
                      <div className="textFontColor fs-16 fw-600">
                        {sanitizeInput(item.item_name ?? "-")}
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <span className="text-gray fs-12 fw-500">
                          {sanitizeInput(item.item_code ?? "")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6 col-lg-3 ms-3 d-flex flex-column flex-grow-1 align-items-start justify-content-start me-1 product-grid-divider">
                    <div className="d-flex flex-column flex-grow-1 align-items-start">
                      <div className="fs-12 fw-600 cardTextGrey">
                        WEIGHT :{" "}
                        <span className="fs-14 fw-400 textcolor">
                          {sanitizeNumericInput(item.weight) || "-"}
                        </span>
                      </div>
                      <div className="fs-12 fw-600 cardTextGrey mt-1">
                        DIMENSION :{" "}
                        <span className="fs-14 fw-400 textcolor">
                          {item.length != null ||
                            item.width != null ||
                            item.height != null
                            ? `${sanitizeNumericInput(item.length) || "-"} x ${sanitizeNumericInput(item.width) || "-"} x ${sanitizeNumericInput(item.height) || "-"}`
                            : sanitizeInput(item.dimension ?? "-")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-row flex-grow-1 align-items-end justify-content-end gap-2 ps-3">
                    <div onClick={(e) => e.stopPropagation()}>
                      {(checkPermission("edit_permission") ||
                        checkPermission("delete_permission")) && (
                          <Dropdown>
                            <Dropdown.Toggle
                              bsPrefix="custom-toggle"
                              variant="light"
                              id="dropdown-sort"
                              className="d-flex align-items-center justify-content-between rounded-5 bg-white product-grid-more-toggle"
                            >
                              <span className="d-flex align-items-center px-1 gap-2">
                                <img src={moreIcon} alt="more" />
                              </span>
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="product-grid-more-menu">
                              {checkPermission("edit_permission") && (
                                <Dropdown.Item
                                  className="fs-14 fw-500 textcolor"
                                  onClick={() => {
                                    navigate("/product/create", {
                                      state: { id: item.id },
                                    });
                                  }}
                                >
                                  Edit
                                </Dropdown.Item>
                              )}
                              {checkPermission("delete_permission") && (
                                <Dropdown.Item
                                  className="fs-14 fw-500 textcolor"
                                  onClick={() => {
                                    setDeleteId(item.id);
                                  }}
                                >
                                  Delete
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                    </div>
                  </div>
                </div>
              )))}

          {/* ---------- Pagination ---------- */}
          <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <Dropdown>
              <span className="pagination-color">Records per page: &nbsp;</span>
              <Dropdown.Toggle
                id="dropdown-basic"
                className="bg-transparent pagination-color border-0 custom-dropdown-toggle p-0"
              >
                <span className="bg-white textcolor no-of-rows rounded-3 px-2 py-2">
                  {itemsPerPage} <DownArrowIc />
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="my-2 product-page-size-menu">
                {[10, 25, 50, 75, 100].map((size) => (
                  <Dropdown.Item
                    key={size}
                    onClick={() => setItemsPerPage(size)}
                    active={itemsPerPage === size}
                    className="text-center"
                  >
                    {size}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <div className="d-flex align-items-center gap-2">
              <ReactPaginate
                previousLabel={
                  <div className="rounded-5 bg-white d-flex align-items-center justify-content-center product-paginate-arrow">
                    <LarIc />
                  </div>
                }
                nextLabel={
                  <div className="rounded-5 bg-white d-flex align-items-center justify-content-center product-paginate-arrow">
                    <RarIc />
                  </div>
                }
                breakLabel={"..."}
                pageCount={pageCount + 1}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                activeClassName={"active"}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Snackbar ---------- */}
      <CommonSnackbar
        show={snackbar.show}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        isSuccess={snackbar.status}
      />
    </>
  );
};

export default ProductListing;
